"use client";

import Button from "@/components/ui/Button";
import VariantSelector from "@/features/product/components/VariantSelector/VariantSelector";
import useLocation from "@/lib/centra/hooks/useLocation";
import usePriceList from "@/lib/centra/hooks/useSelection/cart/usePriceList";

import {
  eventProductDetail,
  generateGTagItem,
} from "@/lib/utils/Analytics/Functions";
import { Suspense, useEffect } from "react";

import { FavouriteIcon } from "@/components/icons/FavouriteIcon";
import { SizeChartModal } from "@/features/product/components/SizeChartModal/SizeChartModal";
import { PDPProduct } from "@/features/product/types";
import { useWishlistProduct } from "@/features/wishlist/hooks";
import useModal from "@/hooks/useModal";
import useSizes from "@/lib/centra/hooks/useSizes";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useProductContext } from "../../ProductProvider";
import NotifyBackInStock, {
  BACK_IN_STOCK_MODAL_KEY,
} from "../NotifyBackInStockModal/NotifyBackInStockModal";
import NotifyComingSoon, {
  COMING_SOON_MODAL_KEY,
} from "../NotifyComingSoon/NotifyComingSoon";
import SizeSelector from "../SizeSelector/SizeSelector";
import { SizeGuideButton } from "./SizeGuide";
import { useProductButtonLabel } from "./getButtonLabel";
import styles from "./index.module.css";
import { useAddToCart } from "./useAddToCart";
export type Sizes = ReturnType<typeof useSizes>;

const ProductActions = ({
  productData,
  displayFindYourFit,
  displaySizeChart,
}: {
  productData: PDPProduct;
  displayFindYourFit: boolean;
  displaySizeChart: boolean;
}) => {
  const t = useTranslations();
  const { activeVariant, product } = useProductContext();
  const { currency } = useLocation();
  const { toggleModal } = useModal("notify");
  const prices = usePriceList(activeVariant?.prices);
  const sizes = useSizes(activeVariant?.sizes);
  const buttonLabel = useProductButtonLabel({ sizes, product: activeVariant });
  const wishlist = useWishlistProduct(product?.itemId);

  const { addToCart, isLoading } = useAddToCart({
    sizes,
    product: activeVariant,
    setModalOpen: toggleModal,
    prices,
  });

  useEffect(() => {
    if (eventProductDetail) {
      if (!currency || !activeVariant || !prices.priceAsNumber) {
        return;
      }
      eventProductDetail({
        currency: currency || "",
        items: [generateGTagItem(activeVariant, prices?.priceAsNumber)],
      });
    }
  }, [activeVariant, currency, prices?.priceAsNumber]);

  return (
    <section className={styles.root}>
      <div className={styles.wrapper}>
        {!!product?.genders?.length && (
          <div className={styles.model}>
            <div className={styles.model__inner}>
              <Link
                className={clsx(
                  activeVariant?.gender === "woman" && styles.model__active,
                )}
                href={
                  activeVariant?.gender === "woman"
                    ? "#"
                    : `/product/${product?.genders[0]?.uri}`
                }
              >
                {t("product.woman")}
              </Link>
              <Link
                className={clsx(
                  activeVariant?.gender === "man" && styles.model__active,
                )}
                href={
                  activeVariant?.gender === "man"
                    ? "#"
                    : `/product/${product?.genders[0]?.uri}`
                }
              >
                {t("product.man")}
              </Link>
            </div>
          </div>
        )}
        <VariantSelector product={productData} />
        {!product?.comingSoon && <SizeSelector sizes={sizes} />}
        <Suspense>
          <NotifyBackInStock item={sizes.selectedSize} />
          <NotifyComingSoon item={sizes.selectedSize} />
        </Suspense>

        <div className={styles.disclaimers}>
          <span>{product?.modelDisclaimer}</span>
          {!!product?.itemId && !!activeVariant && (
            <>
              <div className={styles.sizeGuideButtons}>
                {!!displaySizeChart && (
                  <Button
                    mode="link"
                    modal={"sizechart"}
                    className={styles.sizeChartButton}
                  >
                    <p>{t("sizechart.title")}</p>
                  </Button>
                )}
                {!!displayFindYourFit && (
                  <SizeGuideButton
                    sizes={sizes.sizes}
                    productId={product.itemId}
                    activeVariant={activeVariant}
                    addToCartId="add-to-cart"
                  />
                )}
              </div>
              <SizeChartModal sizeguide={productData?.sizeguide} />
            </>
          )}
        </div>

        <div className={styles.addButtonsWrapper}>
          {buttonLabel.comingSoon || buttonLabel.notifyBackInStock ? (
            <Button
              modal={
                buttonLabel.comingSoon
                  ? COMING_SOON_MODAL_KEY
                  : BACK_IN_STOCK_MODAL_KEY
              }
              full
              className={styles.notifyMe}
            >
              {buttonLabel.label}
            </Button>
          ) : (
            <Button
              id="add-to-cart"
              onClick={addToCart}
              disabled={buttonLabel.disabled}
              isLoading={isLoading}
              full
              className={styles.addToCart}
            >
              {buttonLabel.label}
            </Button>
          )}
          <Button
            className={styles.addButton}
            onClick={wishlist.has ? wishlist.remove : wishlist.add}
            aria-label="Add to wishlist"
          >
            <div
              className={clsx(
                wishlist.has ? styles.removeFavourite : styles.addFavourite,
              )}
            >
              <FavouriteIcon />
            </div>
          </Button>
        </div>

        <div className={styles.disclaimers}>
          <div>{t("product.disclaimers.taxes")}</div>
          <div>{t("product.disclaimers.shipping")}</div>
        </div>
      </div>
    </section>
  );
};

const ProductActionsWrapper: typeof ProductActions = (props) => {
  const { activeVariant } = useProductContext();
  return <ProductActions key={activeVariant?.itemId} {...props} />;
};
export default ProductActionsWrapper;
